import React, {useEffect, useState} from 'react';
import {Alert, IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useSearchParams} from 'react-router-dom';
import useLocalStorage from '../hooks/useLocalStorage';
import useSessionStorage from "../hooks/useSessionStorage";
import {useDataView} from "./dataview/useDataView";

type DataType = 'stock' | 'tradeins' | 'orders' | 'consumers';

interface ViewFilterProps {
   dataType: DataType;
   children: (viewId: string | undefined) => React.ReactNode;
}

export const ViewFilter: React.FC<ViewFilterProps> = ({dataType, children}) => {
   const [searchParams, setSearchParams] = useSearchParams();
   const [storedViewId, setStoredViewId] = useSessionStorage<string | undefined>(`viewId-${dataType}`, undefined);
   const [viewId, setViewId] = useState<string | undefined>(undefined);
   const [initialized, setInitialized] = useState(false);
   const {viewName} = useDataView(viewId);


   // Get viewId from the URL or session storage
   const urlViewId = searchParams.get('viewId');
   
   useEffect(() => {
      if (urlViewId) {
         setViewId(urlViewId);
         setStoredViewId(urlViewId);
      } else if (storedViewId) {
         setViewId(storedViewId);
      } else {
         setViewId(undefined);
      }
      // Mark as initialized after first run
      if (!initialized) {
         setInitialized(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [urlViewId, storedViewId, setStoredViewId, initialized]);

   const handleClearView = () => {
      // Remove from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('viewId');
      setSearchParams(newParams);

      // Clear from session storage
      setStoredViewId(undefined);

      // Clear local state
      setViewId(undefined);
   };

   const getDataTypeLabel = () => {
      switch (dataType) {
         case 'stock':
            return 'stock items';
         case 'tradeins':
            return 'trade-ins';
         case 'orders':
            return 'orders';
         case 'consumers':
            return 'consumers';
         default:
            return 'items';
      }
   };

   return (
      <>
         {viewId && (
            <Alert
               severity="info"
               sx={{mb: 2}}
               action={
                  <IconButton
                     aria-label="clear view filter"
                     color="inherit"
                     size="small"
                     onClick={handleClearView}
                  >
                     <CloseIcon fontSize="small"/>
                  </IconButton>
               }
            >
               {viewName
                  ? `Showing the view "${viewName}". Only ${getDataTypeLabel()} included in the view are shown.`
                  : `You are viewing a filtered list of ${getDataTypeLabel()}. Only items included in the current view are shown.`}
            </Alert>
         )}
         {(() => {
            // Wait for initialization to complete before rendering children
            if (!initialized) {
               return null;
            }
            return children(viewId);
         })()}
      </>
   );
};

// Hook version for flexibility
export const useViewFilter = (dataType: DataType): string | undefined => {
   const [searchParams] = useSearchParams();
   const [storedViewId] = useLocalStorage<string | undefined>(`viewId-${dataType}`, undefined);

   const urlViewId = searchParams.get('viewId');
   return urlViewId || storedViewId;
};
